import type { VersionBody } from './billing';

export interface VersionChanges {
    create: VersionBody[];
    update: { productVersionId: string; currentLabel: string; body: Partial<VersionBody> }[];
    delete: { productVersionId: string; currentLabel: string }[];
}

export type VersionWrite =
    | { kind: 'park'; productVersionId: string; label: string }
    | { kind: 'update'; productVersionId: string; body: Partial<VersionBody> }
    | { kind: 'create'; body: VersionBody }
    | { kind: 'delete'; productVersionId: string };

// Improbable as a typed label, and unique per version so two parked rows never
// collide with each other.
const PARKED_LABEL_PREFIX = '__reassigning__';

// Labels this batch hands to a row that does not already hold them.
function incomingLabels(changes: VersionChanges): Set<string> {
    const labels = changes.create.map((body) => body.label);
    for (const { currentLabel, body } of changes.update) {
        if (body.label !== undefined && body.label !== currentLabel) labels.push(body.label);
    }
    return new Set(labels);
}

function versionsReleasingTheirLabel(changes: VersionChanges) {
    return [
        ...changes.delete,
        ...changes.update.filter(
            ({ currentLabel, body }) => body.label !== undefined && body.label !== currentLabel
        )
    ];
}

/** Deletes run last (a product must keep a version) and a label changing hands
 * parks on a placeholder first (unique per product, label). */
export function planVersionWrites(changes: VersionChanges): VersionWrite[] {
    const incoming = incomingLabels(changes);

    const parkings: VersionWrite[] = versionsReleasingTheirLabel(changes)
        .filter(({ currentLabel }) => incoming.has(currentLabel))
        .map(({ productVersionId }) => ({
            kind: 'update',
            productVersionId,
            body: { label: `${PARKED_LABEL_PREFIX}${productVersionId}` }
        }));

    return [
        ...parkings,
        ...changes.update.map(({ productVersionId, body }): VersionWrite => ({
            kind: 'update',
            productVersionId,
            body
        })),
        ...changes.create.map((body): VersionWrite => ({ kind: 'create', body })),
        ...changes.delete.map(({ productVersionId }): VersionWrite => ({
            kind: 'delete',
            productVersionId
        }))
    ];
}
